/**
 * Viewer-local "DD Mon YYYY, HH:MM" (24-hour) stamp, shared by the Updated column
 * and the ETA display so the two never drift. Assumes a valid ISO-8601 string.
 */
export function formatLocalDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MS_PER_HOUR = MS_PER_MINUTE * MINUTES_PER_HOUR;
const MS_PER_DAY = MS_PER_HOUR * HOURS_PER_DAY;
/** Same locale as `formatLocalDateTime`, so a short date reads "5 Oct" like the full stamp's "05 Oct 2026". */
const SHORT_DATE_LOCALE = 'en-GB';
const JUST_NOW_LABEL = 'just now';
const YESTERDAY_LABEL = 'yesterday';
const NO_TIME_LABEL = '';
const MINUTES_SUFFIX = 'm';
const HOURS_SUFFIX = 'h';
const SHOW_YEAR = 'numeric';

function parseIsoMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return ms;
}

function startOfLocalDay(ms: number): number {
  const day = new Date(ms);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

/** Any time on the local calendar day before `nowMs`'s — not merely 24–48h ago. */
function isLocalYesterday(thenMs: number, nowMs: number): boolean {
  const todayStart = startOfLocalDay(nowMs);
  return thenMs < todayStart && thenMs >= todayStart - MS_PER_DAY;
}

/** A date shows its year only when it isn't this year's. */
export function yearOptionFor(then: Date, now: Date): typeof SHOW_YEAR | undefined {
  if (then.getFullYear() === now.getFullYear()) return undefined;
  return SHOW_YEAR;
}

/** "5 Oct" this year; "5 Oct 2025" for another year. */
function shortDate(thenMs: number, nowMs: number): string {
  const then = new Date(thenMs);
  return then.toLocaleDateString(SHORT_DATE_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: yearOptionFor(then, new Date(nowMs)),
  });
}

/**
 * Compact, chat-style relative time: "just now", "42m", "2h", "yesterday",
 * then a short date. `nowMs` is passed in (not read) so a caller can tick it
 * on a timer and tests stay deterministic. A time slightly in the future (a
 * client clock running behind the server's) reads as "just now". Empty for a
 * missing or unparsable stamp.
 */
export function formatShortRelativeTime(iso: string | null | undefined, nowMs: number): string {
  const thenMs = parseIsoMs(iso);
  if (thenMs === null) return NO_TIME_LABEL;
  const elapsedMs = nowMs - thenMs;
  if (elapsedMs < MS_PER_MINUTE) return JUST_NOW_LABEL;
  if (elapsedMs < MS_PER_HOUR) return `${Math.floor(elapsedMs / MS_PER_MINUTE)}${MINUTES_SUFFIX}`;
  if (elapsedMs < MS_PER_DAY) return `${Math.floor(elapsedMs / MS_PER_HOUR)}${HOURS_SUFFIX}`;
  if (isLocalYesterday(thenMs, nowMs)) return YESTERDAY_LABEL;
  return shortDate(thenMs, nowMs);
}
